import { useState, useMemo } from 'react';
import { Send, Ban, Trash2, ChevronDown, ChevronRight, MoreHorizontal, ShieldCheck, ShieldOff, Info, UserPlus, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useQueryable } from '@/lib/queryable';
import { tenantVendorPortalUsersSurface } from '@/lib/queryable/surfaces/tenant-vendor-portal-users.surface';
import { FilterBar, SortPicker, SavedViewPicker } from '@/components/queryable';
import {
  usePendingPortalInvitations,
  useDisablePortalUser,
  useRemovePortalUser,
  useResendPortalInvitation,
  useInviteVendorContact,
  useCancelPortalInvitation,
  type PendingInvitation,
} from '@/hooks/usePortalUsers';
import { formatDate } from '@/lib/utils';
import { useAuthStore } from '@/store/auth.store';

// ── Row types ─────────────────────────────────────────────────────────────────

interface PortalUserRow {
  vendor_account_id: string;
  email: string;
  name: string | null;
  vendor_account_status: string;
  mfa_enabled: boolean;
  last_login_at: string | null;
  created_at: string;
  relationship_id: string;
  relationship_status: string;
  accepted_at: string | null;
  relationship_created_at: string;
  auth_method_state: string;
  federated_providers: string[] | null;
}

// ── §4.2 Invitation status chip (pending_acceptance / revoked / expired) ──────

function InvitationStatusChip({ status }: { status: string }) {
  if (status === 'pending') {
    return (
      <Badge variant="sky" title="Invitation sent; vendor has not accepted yet. Resend or cancel this invitation.">
        Invitation pending
      </Badge>
    );
  }
  if (status === 'revoked') {
    return <Badge variant="muted">Cancelled</Badge>;
  }
  if (status === 'expired') {
    return <Badge variant="muted">Expired</Badge>;
  }
  return <Badge variant="muted">{status}</Badge>;
}

// ── Pending Invitations ────────────────────────────────────────────────────────

function PendingInvitationsSection({ vendorId, canManage }: { vendorId: string; canManage: boolean }) {
  const [expanded, setExpanded] = useState(true);
  const [showCancelled, setShowCancelled] = useState(false);
  const [cancelConfirmId, setCancelConfirmId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const closeCancelDialog = () => { setCancelConfirmId(null); setCancelReason(''); };

  const { data: invitations = [], isLoading } = usePendingPortalInvitations(vendorId, { includeRevoked: showCancelled });
  const resend = useResendPortalInvitation(vendorId);
  const cancel = useCancelPortalInvitation(vendorId);
  const [resendingId, setResendingId] = useState<string | null>(null);

  const cancelTarget = invitations.find((inv) => inv.id === cancelConfirmId);

  const handleResend = async (tokenId: string) => {
    setResendingId(tokenId);
    try {
      await resend.mutateAsync(tokenId);
    } finally {
      setResendingId(null);
    }
  };

  const handleCancelConfirm = () => {
    if (!cancelConfirmId) return;
    cancel.mutate(
      { invitationId: cancelConfirmId, reason: cancelReason.trim() || undefined },
      { onSettled: closeCancelDialog },
    );
  };

  const activeCount = invitations.filter((inv) => inv.status === 'pending').length;

  if (!isLoading && invitations.length === 0 && !showCancelled) return null;

  return (
    <>
      <div className="mb-4 rounded-lg border border-border-subtle bg-bg-surface">
        <button
          type="button"
          className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-text-primary hover:bg-bg-surface-2/50 transition-colors rounded-lg"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? <ChevronDown className="h-4 w-4 text-text-secondary" /> : <ChevronRight className="h-4 w-4 text-text-secondary" />}
          <span>Pending invitations</span>
          {!isLoading && activeCount > 0 && <Badge variant="muted" className="ml-1">{activeCount}</Badge>}
          <label
            className="ml-auto flex items-center gap-1.5 text-xs font-normal text-text-secondary cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              className="rounded"
              checked={showCancelled}
              onChange={(e) => setShowCancelled(e.target.checked)}
            />
            Show cancelled invitations
          </label>
        </button>

        {expanded && (
          <div className="border-t border-border-subtle">
            {isLoading ? (
              <div className="p-4 space-y-2">
                <Skeleton className="h-8 rounded" />
                <Skeleton className="h-8 rounded" />
              </div>
            ) : invitations.length === 0 ? (
              <p className="px-4 py-3 text-xs text-text-secondary">No invitations to show.</p>
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="bg-bg-surface-2">
                  <tr>
                    <th className="py-2 px-4 text-xs font-medium text-text-secondary">Email</th>
                    <th className="py-2 px-4 text-xs font-medium text-text-secondary">Status</th>
                    <th className="py-2 px-4 text-xs font-medium text-text-secondary">Sent</th>
                    <th className="py-2 px-4 text-xs font-medium text-text-secondary">Expires</th>
                    <th className="py-2 px-4 text-xs font-medium text-text-secondary">Invited by</th>
                    <th className="py-2 px-4 text-xs font-medium text-text-secondary" />
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((inv: PendingInvitation) => (
                    <tr key={inv.id} className="border-t border-border-subtle hover:bg-bg-surface-2/30">
                      <td className="py-2 px-4 text-text-primary">{inv.email}</td>
                      <td className="py-2 px-4"><InvitationStatusChip status={inv.status} /></td>
                      <td className="py-2 px-4 text-text-secondary">{formatDate(inv.created_at)}</td>
                      <td className="py-2 px-4 text-text-secondary">{formatDate(inv.expires_at)}</td>
                      <td className="py-2 px-4 text-text-secondary">{inv.invited_by_email ?? '—'}</td>
                      <td className="py-2 px-4 text-right">
                        {inv.status === 'pending' && canManage && (
                          <div className="flex items-center gap-1 justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              disabled={resendingId === inv.id}
                              onClick={() => handleResend(inv.id)}
                              title="Send a new invitation email. The previous invitation link will stop working immediately."
                            >
                              <Send className="h-3 w-3 mr-1" />
                              Resend invitation
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                              onClick={() => setCancelConfirmId(inv.id)}
                              title="Withdraw this invitation. The link will stop working immediately. The vendor will not receive a notification."
                            >
                              <XCircle className="h-3 w-3 mr-1" />
                              Cancel invitation
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Cancel confirm dialog */}
      <Dialog open={!!cancelConfirmId} onOpenChange={(o) => { if (!o) closeCancelDialog(); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Cancel this invitation?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-text-secondary">
            The invitation link for <strong className="text-text-primary">{cancelTarget?.email}</strong> will stop
            working immediately. The vendor will not receive a notification.
          </p>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-text-secondary">Reason (optional)</label>
            <textarea
              className="w-full rounded border border-border bg-bg-surface px-3 py-2 text-sm text-text-primary resize-none focus:outline-none focus:ring-1 focus:ring-ring"
              rows={2}
              placeholder="Enter reason for cancellation…"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              maxLength={500}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeCancelDialog} disabled={cancel.isPending}>
              Keep invitation
            </Button>
            <Button variant="destructive" onClick={handleCancelConfirm} disabled={cancel.isPending}>
              {cancel.isPending ? 'Cancelling…' : 'Yes, cancel invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── §4.2 Status badge (relationship row) ──────────────────────────────────────

const RELATIONSHIP_STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  disabled_by_tenant: 'Disabled',
  removed: 'Removed',
  lapsed: 'Lapsed',
  superseded: 'Superseded',
  pending_acceptance: 'Pending acceptance',
};

function RelationshipStatusBadge({ status }: { status: string }) {
  if (status === 'active') return <Badge variant="default">Active</Badge>;
  if (status === 'disabled_by_tenant') return <Badge variant="amber">Disabled</Badge>;
  if (status === 'pending_acceptance') {
    return (
      <Badge variant="sky" title="Relationship row exists; invitation accepted or not is governed by the linked invitation token.">
        Pending acceptance
      </Badge>
    );
  }
  return <Badge variant="muted">{RELATIONSHIP_STATUS_LABELS[status] ?? status}</Badge>;
}

// ── Confirm dialogs ───────────────────────────────────────────────────────────

interface DisableDialogProps {
  open: boolean;
  email: string;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}

function DisablePortalUserDialog({ open, email, onConfirm, onCancel, isPending }: DisableDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Disable portal access?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-text-secondary">
          <strong>{email}</strong> will be blocked from logging into the vendor portal. Their account is
          preserved; access can be restored later.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isPending}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isPending}>Disable access</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface RemoveDialogProps {
  open: boolean;
  email: string;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}

function RemovePortalUserDialog({ open, email, onConfirm, onCancel, isPending }: RemoveDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove from portal?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-text-secondary">
          This will end <strong>{email}</strong>'s portal access for your tenant. The relationship is marked
          removed; the account itself remains in the vendor's profile.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isPending}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isPending}>Remove from portal</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Invite modal state ────────────────────────────────────────────────────────

interface InviteFormState {
  email: string;
  firstName: string;
  lastName: string;
  roleTitle: string;
}

const EMPTY_INVITE: InviteFormState = { email: '', firstName: '', lastName: '', roleTitle: '' };

// ── Main tab ─────────────────────────────────────────────────────────────────

export function PortalUsersTab({ vendorId }: { vendorId: string }) {
  const qbl = useQueryable<PortalUserRow>({
    surface: tenantVendorPortalUsersSurface,
    fetchUrl: `/vendors/${vendorId}/portal-users`,
    syncToUrl: false,
  });

  const currentViewConfig = useMemo(() => ({
    sort: qbl.queryState.sort,
    filters: qbl.queryState.filters,
    viewMode: qbl.queryState.viewMode,
  }), [qbl.queryState]);

  const { hasAnyRole } = useAuthStore();
  const canManage = hasAnyRole(['risk_manager', 'ciso', 'super_admin']);

  const disable = useDisablePortalUser(vendorId);
  const remove = useRemovePortalUser(vendorId);
  const invite = useInviteVendorContact(vendorId);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState<InviteFormState>(EMPTY_INVITE);
  const [confirmDisableId, setConfirmDisableId] = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  const targetRow = qbl.data.find(
    (r) => r.vendor_account_id === (confirmDisableId ?? confirmRemoveId),
  );

  const handleInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      email: inviteForm.email.trim(),
      ...(inviteForm.firstName.trim() ? { firstName: inviteForm.firstName.trim() } : {}),
      ...(inviteForm.lastName.trim() ? { lastName: inviteForm.lastName.trim() } : {}),
      ...(inviteForm.roleTitle.trim() ? { roleTitle: inviteForm.roleTitle.trim() } : {}),
    };
    await invite.mutateAsync(payload);
    setInviteForm(EMPTY_INVITE);
    setInviteOpen(false);
  };

  return (
    <div className="space-y-4">
      {/* Portal-access info banner */}
      <div className="flex items-start gap-2.5 rounded-lg border border-border-subtle bg-bg-surface px-4 py-3 text-xs text-text-secondary">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-text-tertiary" />
        <span>
          Vendor portal accounts are self-managed. Use{' '}
          <strong className="text-text-primary font-medium">Disable access</strong> to block access without
          removing the contact record. Use{' '}
          <strong className="text-text-primary font-medium">Remove from portal</strong> to end this user's
          portal access for your tenant. The relationship is marked removed; the account itself remains.
        </span>
      </div>

      {/* Pending invitations — auto-expanded when count > 0 */}
      <PendingInvitationsSection vendorId={vendorId} canManage={canManage} />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {canManage && (
          <Button size="sm" onClick={() => setInviteOpen(true)}>
            <UserPlus className="h-4 w-4 mr-1.5" />
            Invite Contact
          </Button>
        )}
        <div className="flex items-center gap-2 ml-auto">
          <FilterBar
            filterOptions={tenantVendorPortalUsersSurface.filterOptions}
            activeFilters={qbl.queryState.filters}
            onSetFilter={qbl.setFilter}
            onClearFilter={qbl.clearFilter}
            onClearAll={qbl.clearAllFilters}
          />
          <SortPicker
            options={tenantVendorPortalUsersSurface.sortOptions}
            current={qbl.queryState.sort}
            onChange={qbl.setSort}
          />
          <SavedViewPicker
            surfaceKey={tenantVendorPortalUsersSurface.surfaceKey}
            savedViews={qbl.savedViews}
            onLoad={qbl.loadSavedView}
            currentViewConfig={currentViewConfig}
          />
        </div>
      </div>

      {/* Loading */}
      {qbl.isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10 rounded" />)}
        </div>
      )}

      {/* Empty */}
      {!qbl.isLoading && qbl.data.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border-subtle bg-bg-surface/50 px-6 py-12 text-center">
          <p className="text-sm font-medium text-text-primary">No portal users</p>
          <p className="mt-1 text-xs text-text-secondary">Invite a vendor to create a portal account and they will appear here.</p>
        </div>
      )}

      {/* Table */}
      {!qbl.isLoading && qbl.data.length > 0 && (
        <div className="rounded-lg border border-border-subtle overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="bg-bg-surface-2 border-b border-border-subtle">
              <tr>
                <th className="py-2 px-4 text-xs font-medium text-text-secondary">Email</th>
                <th className="py-2 px-4 text-xs font-medium text-text-secondary">Name</th>
                <th className="py-2 px-4 text-xs font-medium text-text-secondary">Status</th>
                <th className="py-2 px-4 text-xs font-medium text-text-secondary">Auth</th>
                <th className="py-2 px-4 text-xs font-medium text-text-secondary">MFA</th>
                <th className="py-2 px-4 text-xs font-medium text-text-secondary">Last login</th>
                <th className="py-2 px-4 text-xs font-medium text-text-secondary" />
              </tr>
            </thead>
            <tbody>
              {qbl.data.map((row) => (
                <tr key={row.vendor_account_id} className="border-b border-border-subtle hover:bg-bg-surface-2/50 transition-colors">
                  <td className="py-3 px-4 text-text-primary font-medium">{row.email}</td>
                  <td className="py-3 px-4 text-text-secondary">{row.name ?? '—'}</td>
                  <td className="py-3 px-4"><RelationshipStatusBadge status={row.relationship_status} /></td>
                  <td className="py-3 px-4 text-text-secondary capitalize">{row.auth_method_state}</td>
                  <td className="py-3 px-4">
                    {row.mfa_enabled
                      ? <ShieldCheck className="h-4 w-4 text-green-500" />
                      : <ShieldOff className="h-4 w-4 text-text-tertiary" />}
                  </td>
                  <td className="py-3 px-4 text-text-secondary">
                    {row.last_login_at ? formatDate(row.last_login_at) : '—'}
                  </td>
                  <td className="py-3 px-4 text-right">
                    {canManage && (row.relationship_status === 'active' || row.relationship_status === 'disabled_by_tenant') && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {row.relationship_status === 'active' && (
                            <DropdownMenuItem
                              className="text-amber-600"
                              onClick={() => setConfirmDisableId(row.vendor_account_id)}
                              title="Block this user from logging into the vendor portal. Their account is preserved; access can be restored later."
                            >
                              <Ban className="h-4 w-4 mr-2" />
                              Disable access
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => setConfirmRemoveId(row.vendor_account_id)}
                            title="End this user's portal access for your tenant. The relationship is marked removed; the account itself remains in the vendor's profile."
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Remove from portal
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {qbl.meta && qbl.meta.totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-text-secondary">
          <span>{qbl.meta.total} users — page {qbl.meta.page} of {qbl.meta.totalPages}</span>
          <div className="flex gap-2">
            <button
              disabled={!qbl.meta.hasPrev}
              onClick={() => qbl.setPage(qbl.meta!.page - 1)}
              className="text-sm disabled:opacity-40 hover:text-text-primary"
            >← Previous</button>
            <button
              disabled={!qbl.meta.hasNext}
              onClick={() => qbl.setPage(qbl.meta!.page + 1)}
              className="text-sm disabled:opacity-40 hover:text-text-primary"
            >Next →</button>
          </div>
        </div>
      )}

      {/* §4.1 Invite Contact modal — email (required) + firstName/lastName/roleTitle (optional) */}
      <Dialog open={inviteOpen} onOpenChange={(o) => { if (!o) { setInviteOpen(false); setInviteForm(EMPTY_INVITE); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Invite Vendor Contact</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleInviteSubmit} className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="vc-invite-email">Email <span className="text-danger">*</span></Label>
              <Input
                id="vc-invite-email"
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
                required
                placeholder="vendor@example.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="vc-invite-first">First name <span className="text-text-tertiary text-xs font-normal">(optional)</span></Label>
              <Input
                id="vc-invite-first"
                type="text"
                value={inviteForm.firstName}
                onChange={(e) => setInviteForm((f) => ({ ...f, firstName: e.target.value }))}
                placeholder="Jane"
                maxLength={100}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="vc-invite-last">Last name <span className="text-text-tertiary text-xs font-normal">(optional)</span></Label>
              <Input
                id="vc-invite-last"
                type="text"
                value={inviteForm.lastName}
                onChange={(e) => setInviteForm((f) => ({ ...f, lastName: e.target.value }))}
                placeholder="Smith"
                maxLength={100}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="vc-invite-role">
                Role / title <span className="text-text-tertiary text-xs font-normal">(optional)</span>
              </Label>
              <Input
                id="vc-invite-role"
                type="text"
                value={inviteForm.roleTitle}
                onChange={(e) => setInviteForm((f) => ({ ...f, roleTitle: e.target.value }))}
                placeholder="Security Engineer"
                maxLength={150}
              />
              <p className="text-xs text-text-tertiary">Their role at the vendor — e.g. 'Security Engineer'</p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                type="button"
                onClick={() => { setInviteOpen(false); setInviteForm(EMPTY_INVITE); }}
              >Cancel</Button>
              <Button type="submit" disabled={invite.isPending}>
                {invite.isPending ? 'Sending…' : 'Send Invitation'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Disable confirm */}
      <DisablePortalUserDialog
        open={!!confirmDisableId}
        email={targetRow?.email ?? ''}
        isPending={disable.isPending}
        onCancel={() => setConfirmDisableId(null)}
        onConfirm={() => {
          if (confirmDisableId) {
            disable.mutate(confirmDisableId, { onSuccess: () => setConfirmDisableId(null) });
          }
        }}
      />

      {/* Remove confirm */}
      <RemovePortalUserDialog
        open={!!confirmRemoveId}
        email={targetRow?.email ?? ''}
        isPending={remove.isPending}
        onCancel={() => setConfirmRemoveId(null)}
        onConfirm={() => {
          if (confirmRemoveId) {
            remove.mutate(confirmRemoveId, { onSuccess: () => setConfirmRemoveId(null) });
          }
        }}
      />
    </div>
  );
}
